/* eslint-disable @typescript-eslint/no-explicit-any */
import ApiClient from 'api/ApiClient';
import { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { CreateDataExport, DataExport } from 'model/dataExports';
import {
    DATA_EXPORTS_ERROR,
    DATA_EXPORTS_RESET,
    DATA_EXPORTS_FETCH_START,
    DATA_EXPORTS_FETCH_SUCCESS,
    DATA_EXPORTS_CREATE_START,
    DATA_EXPORTS_CREATE_SUCCESS,
    DataExportsAction
} from './DataExportsActionsTypes';
import { convertToCreateDataExportDto, convertToDataExport } from './DataExportsConverter';
import { DataExportDto, DataExportsResponseDto } from './DataExportsDtos';
import { MockLocation, isDataExportsMockEnabled, mockCreateDataExport, mockFetchDataExports } from './DataExportsMock';
import { showErrorThunk } from '@spike/notifications-action';

const dataExportsUrl = (organizationId: number): string => `/organizations/${organizationId}/exports`;

const extractErrorMessage = (apiError: any): string => {
    const data = apiError?.response?.data;

    if (typeof data?.error === 'string') return data.error;
    if (typeof data?.message === 'string') return data.message;
    if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors[0];

    return 'Something went wrong with the export. Please try again.';
};

// TEMPORARY (mock): the organization's locations, so the mock files carry real location names.
const mockLocations = (state: RootState): Array<MockLocation> =>
    state.emailMarketing.locations.map(location => ({ id: location.id, name: location.name }));

const error = (err: string): DataExportsAction => ({
    type: DATA_EXPORTS_ERROR,
    payload: {
        error: err
    }
});

export const resetDataExportsState = (): DataExportsAction => ({
    type: DATA_EXPORTS_RESET
});

/**
 * Fetch exports - GET
 * Every export of the organization, newest first. `silent` skips the loading
 * state so the list doesn't flash while it's being polled.
 */
export const fetchDataExportsThunk = (
    organizationId: number,
    { silent = false }: { silent?: boolean } = {}
): ThunkAction<Promise<Array<DataExport>>, RootState, null, DataExportsAction> => {
    return async (dispatch, getState) => {
        if (!silent) {
            dispatch({ type: DATA_EXPORTS_FETCH_START });
        }

        try {
            let dtos: Array<DataExportDto>;

            if (isDataExportsMockEnabled()) {
                dtos = await mockFetchDataExports(mockLocations(getState()));
            } else {
                const { data }: AxiosResponse<DataExportsResponseDto> = await ApiClient.get(
                    dataExportsUrl(organizationId)
                );
                dtos = data.exports;
            }

            const dataExports = dtos.map(convertToDataExport);

            dispatch({
                type: DATA_EXPORTS_FETCH_SUCCESS,
                payload: { dataExports }
            });

            return dataExports;
        } catch (apiError) {
            dispatch(error(extractErrorMessage(apiError)));
            throw apiError;
        }
    };
};

/**
 * Create export - POST
 * The file is generated in the background; the export comes back pending and
 * the list is polled until it finishes.
 */
export const createDataExportThunk = (
    organizationId: number,
    dataExport: CreateDataExport
): ThunkAction<Promise<DataExport>, RootState, null, DataExportsAction> => {
    return async (dispatch, getState) => {
        dispatch({ type: DATA_EXPORTS_CREATE_START });

        const payload = convertToCreateDataExportDto(dataExport);

        try {
            let dto: DataExportDto;

            if (isDataExportsMockEnabled()) {
                const user = getState().login.auth?.user;
                dto = await mockCreateDataExport(
                    payload,
                    user ? `${user.firstName} ${user.lastName}` : 'You',
                    mockLocations(getState())
                );
            } else {
                const { data }: AxiosResponse<DataExportDto> = await ApiClient.post(
                    dataExportsUrl(organizationId),
                    payload
                );
                dto = data;
            }

            const created = convertToDataExport(dto);

            dispatch({
                type: DATA_EXPORTS_CREATE_SUCCESS,
                payload: { dataExport: created }
            });

            return created;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            throw apiError;
        }
    };
};
